(() => {
  const initSection = (section) => {
    const form = section.querySelector('[data-custom-slipcover-form]');
    if (!form || form.dataset.initialized === 'true') return;

    form.dataset.initialized = 'true';

    const panels = Array.from(form.querySelectorAll('[data-form-step]'));
    const progressItems = Array.from(form.querySelectorAll('[data-progress-step]'));
    const status = form.querySelector('[data-form-status]');
    const photoStatus = form.querySelector('[data-photo-status]');
    const photoLink = form.querySelector('[data-photo-link]');
    const photoInputs = Array.from(form.querySelectorAll('[data-photo-input]'));
    const review = form.querySelector('[data-form-review]');
    const submitButton = form.querySelector('[data-form-submit]');
    const successTemplate = form.querySelector('[data-form-success-template]');
    let activeStep = 1;

    const contactPosted = new URLSearchParams(window.location.search).get('contact_posted') === 'true';
    if (contactPosted && successTemplate) {
      form.replaceChildren(successTemplate.content.cloneNode(true));
      requestAnimationFrame(() => {
        form.querySelector('.custom-slipcovers__form-message--success')?.focus();
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }

    const externalEndpoint = () => form.dataset.externalEndpoint?.trim() || '';

    const fieldValue = (name) => {
      const field = form.elements.namedItem(name);
      if (!field) return '';
      return typeof field.value === 'string' ? field.value.trim() : '';
    };

    const selectedFiles = () =>
      photoInputs
        .filter((input) => input.files && input.files.length)
        .map((input) => input.files[0]);

    const setStatus = (message, isError = false) => {
      if (!status) return;
      status.textContent = message;
      status.style.color = isError ? '#a93030' : '';
    };

    const setPhotoStatus = (message = '', isError = false) => {
      if (!photoStatus) return;
      photoStatus.textContent = message;
      photoStatus.style.color = isError ? '#a93030' : '';
    };

    const updateReview = () => {
      if (!review) return;

      const files = selectedFiles();
      const shareLink = photoLink?.value.trim() || '';
      let photoSummary = shareLink ? 'Share link provided' : 'Not provided';

      if (files.length) {
        photoSummary = `${files.length} file${files.length === 1 ? '' : 's'} selected`;
      }

      const rows = [
        ['Brand', fieldValue('contact[furniture_brand]') || 'Not provided'],
        ['Model', fieldValue('contact[furniture_model]') || 'Not provided'],
        ['Furniture', fieldValue('contact[furniture_type]') || 'Not provided'],
        [
          'Approx. width',
          `${fieldValue('contact[total_width]') || '—'} ${fieldValue('contact[measurement_unit]') || ''}`.trim(),
        ],
        ['Name', fieldValue('contact[name]') || 'Not provided'],
        ['Email', fieldValue('contact[email]') || 'Not provided'],
        ['Photos', photoSummary],
        ['Country', fieldValue('contact[country]') || 'Not provided'],
      ];

      review.replaceChildren(
        ...rows.map(([label, value]) => {
          const item = document.createElement('div');
          item.className = 'custom-slipcovers__review-item';

          const itemLabel = document.createElement('span');
          itemLabel.textContent = label;

          const itemValue = document.createElement('strong');
          itemValue.textContent = value;

          item.append(itemLabel, itemValue);
          return item;
        }),
      );
    };

    const setStep = (nextStep) => {
      activeStep = Math.max(1, Math.min(nextStep, panels.length));

      panels.forEach((panel) => {
        const panelStep = Number(panel.dataset.formStep);
        const isActive = panelStep === activeStep;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
      });

      progressItems.forEach((item) => {
        const itemStep = Number(item.dataset.progressStep);
        item.classList.toggle('is-active', itemStep === activeStep);
        item.classList.toggle('is-complete', itemStep < activeStep);
        if (itemStep === activeStep) {
          item.setAttribute('aria-current', 'step');
        } else {
          item.removeAttribute('aria-current');
        }
      });

      if (activeStep === panels.length) updateReview();

      const heading = panels[activeStep - 1]?.querySelector('h3');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }

      form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setStatus('');
    };

    const visibleRequiredFields = () =>
      Array.from(panels[activeStep - 1]?.querySelectorAll('[required]') || []);

    const validatePhotos = () => {
      if (activeStep !== 3) return true;

      const files = selectedFiles();
      const shareLink = photoLink?.value.trim() || '';

      if (files.length || shareLink) {
        setPhotoStatus('');
        return true;
      }

      const message = 'Please upload at least one clear photo or add a public photo share link.';

      setPhotoStatus(message, true);
      photoLink?.focus();
      return false;
    };

    const validateStep = () => {
      const fields = visibleRequiredFields();

      for (const field of fields) {
        field.setAttribute('aria-invalid', field.checkValidity() ? 'false' : 'true');
        if (!field.checkValidity()) {
          field.reportValidity();
          field.focus();
          return false;
        }
      }

      return validatePhotos();
    };

    form.addEventListener('click', (event) => {
      const nextButton = event.target.closest('[data-form-next]');
      const backButton = event.target.closest('[data-form-back]');

      if (nextButton && validateStep()) setStep(activeStep + 1);
      if (backButton) setStep(activeStep - 1);
    });

    form.querySelectorAll('[required]').forEach((field) => {
      field.addEventListener('input', () => {
        if (field.checkValidity()) field.removeAttribute('aria-invalid');
      });
      field.addEventListener('change', () => {
        if (field.checkValidity()) field.removeAttribute('aria-invalid');
      });
    });

    photoLink?.addEventListener('input', () => setPhotoStatus(''));

    photoInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const upload = input.closest('.custom-slipcovers__upload');
        const fileName = upload?.querySelector('[data-file-name]');
        const file = input.files && input.files[0];

        upload?.classList.toggle('has-file', Boolean(file));
        if (fileName) fileName.textContent = file ? file.name : 'No file selected';
        if (file) setPhotoStatus('');
      });
    });

    form.addEventListener('submit', async (event) => {
      if (activeStep !== panels.length) {
        event.preventDefault();
        if (validateStep()) setStep(activeStep + 1);
        return;
      }

      if (!form.checkValidity()) return;

      const submittedAt = form.querySelector('[data-quote-submitted-at]');
      if (submittedAt) submittedAt.value = new Date().toISOString();

      const endpoint = externalEndpoint();
      if (!endpoint) {
        setStatus('Sending your request…');
        return;
      }

      event.preventDefault();
      submitButton?.setAttribute('disabled', 'disabled');
      submitButton?.setAttribute('aria-busy', 'true');
      setStatus('Uploading your details and photos…');

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          body: new FormData(form),
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

        const successMessage = successTemplate?.content.cloneNode(true);
        if (successMessage) {
          form.replaceChildren(successMessage);
        }
        form.querySelector('.custom-slipcovers__form-message')?.focus();
      } catch (error) {
        setStatus('We could not send the request. Please try again or contact us directly.', true);
        submitButton?.removeAttribute('disabled');
        submitButton?.removeAttribute('aria-busy');
      }
    });
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-custom-slipcovers]').forEach(initSection);
    if (root.matches?.('[data-custom-slipcovers]')) initSection(root);
  };

  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
